import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, UserCircle, Crown, MapTrifold, PenNib, Key, Prohibit, CheckCircle, IdentificationBadge } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const ROLE_ICON = { owner: Crown, field_executive: MapTrifold, report_writer: PenNib };
const EMPTY = { name: "", email: "", password: "", role: "field_executive", phone: "" };

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [resetFor, setResetFor] = useState(null); // {id, name}
  const [resetPw, setResetPw] = useState("");
  const isOwner = user?.role === "owner";

  const load = () => api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team", form);
      toast.success("Team member added");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/team/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateMember = async (id, patch) => {
    try { await api.put(`/team/${id}`, patch); load(); toast.success("Member updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/team/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null); setResetPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">People &amp; Administration</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">{team.length} members · manage roles, access & passwords</p>
        </div>
        {isOwner && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-member-btn" className="rounded-full bg-primary hover:bg-[#113A64]"><Plus className="mr-1" size={16} /> Add Member</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {team.map((m) => {
          const Icon = ROLE_ICON[m.role] || UserCircle;
          const inactive = m.is_active === false;
          const self = m.id === user.id;
          return (
            <Card key={m.id} className={`p-5 border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg ${inactive ? "opacity-60" : ""}`} data-testid={`member-${m.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/5 flex items-center justify-center shrink-0"><Icon size={24} weight="duotone" className="text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-primary truncate">{m.name}{self && <span className="text-xs text-muted-foreground font-normal"> (you)</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-accent">{ROLE_LABELS[m.role] || m.role}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${inactive ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{inactive ? "Inactive" : "Active"}</span>
                  </div>
                  {m.last_login_at && <div className="text-[10px] text-muted-foreground mt-0.5">Last login {new Date(m.last_login_at).toLocaleDateString("en-IN")}</div>}
                </div>
              </div>

              {isOwner && (
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                  <Select value={m.role} onValueChange={(v) => updateMember(m.id, { role: v })} disabled={self}>
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-[120px]" data-testid={`member-role-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="field_executive">Field Executive</SelectItem>
                      <SelectItem value="report_writer">Report Writer</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="outline" className="h-8 w-8" title="Employee profile, salary & visits" onClick={() => navigate(`/app/employees?uid=${m.id}&tab=profile`)} data-testid={`profile-${m.id}`}><IdentificationBadge size={15} /></Button>
                  <Button size="icon" variant="outline" className="h-8 w-8" title="Reset password" onClick={() => { setResetFor({ id: m.id, name: m.name }); setResetPw(""); }} data-testid={`reset-pw-${m.id}`}><Key size={15} /></Button>
                  {!self && (
                    <Button size="icon" variant="outline" className={`h-8 w-8 ${inactive ? "text-emerald-600" : "text-amber-600"}`} title={inactive ? "Activate" : "Deactivate"} onClick={() => updateMember(m.id, { is_active: inactive })} data-testid={`toggle-active-${m.id}`}>
                      {inactive ? <CheckCircle size={15} /> : <Prohibit size={15} />}
                    </Button>
                  )}
                  {!self && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="icon" variant="outline" className="h-8 w-8 text-destructive" data-testid={`delete-member-${m.id}`}><Trash size={15} /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove {m.name}?</AlertDialogTitle><AlertDialogDescription>They will lose access to the workspace.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(m.id)} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add Team Member</DialogTitle><DialogDescription>Create a login for a colleague and set their role.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="member-form">
            <div className="space-y-1.5"><Label>Name *</Label><Input required value={form.name} onChange={set("name")} data-testid="member-name" /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="member-email" /></div>
            <div className="space-y-1.5"><Label>WhatsApp Phone <span className="text-[10px] text-muted-foreground">(for job & reminder alerts, with country code e.g. +9199...)</span></Label><Input type="tel" value={form.phone} onChange={set("phone")} placeholder="+91XXXXXXXXXX" data-testid="member-phone" /></div>
            <div className="space-y-1.5"><Label>Password *</Label><Input type="password" required value={form.password} onChange={set("password")} placeholder="Min 8 chars, a letter & a number" data-testid="member-password" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={set("role")}><SelectTrigger data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="field_executive">Field Executive</SelectItem>
                  <SelectItem value="report_writer">Report Writer</SelectItem>
                  <SelectItem value="accountant">Accountant (invoices only)</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent></Select>
            </div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-member-btn">Add Member</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reset Password</DialogTitle><DialogDescription>Set a new password for {resetFor?.name}. They'll be signed out everywhere.</DialogDescription></DialogHeader>
          <form onSubmit={doReset} className="space-y-3" data-testid="reset-pw-form">
            <div className="space-y-1.5"><Label>New Password</Label><Input type="password" required value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Min 8 chars, a letter & a number" data-testid="reset-pw-input" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-reset-pw-btn">Reset Password</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
